import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { compose } from 'redux';

// Plate-based editor
import { PlateReportEditor } from './plate';
import translations from './translations';

// Actions
import { getGeneralDashboardData, getDepartmentDashboardData } from '../../../store/actions/dashboardActions';
import { getRisks_Report, getControls_Report, getActions_Report, getIncidents_Report, getLevels } from '../../../store/actions/riskActions';
import { createReport, getReport, saveReport } from '../../../store/actions/reportActions';

// Assets
import spinner from '../../../assets/images/loader_blue.svg';

// Styles
import './ReportEditor.css';

// BLOCK_TYPES constant for backward compatibility
const BLOCK_TYPES = {
  TEXT: 'text',
  IMAGE: 'image',
  CHART: 'chart',
  TABLE: 'table',
  HERO: 'hero',
  DIVIDER: 'divider',
  COLUMNS: 'columns',
};

// Main Report Editor Component (Wrapped)
function ReportEditorInner({
  department_id,
  closeReport,
  general_dashboard_data,
  department_dashboard_data,
  risks_report,
  risk_controls,
  risk_actions,
  risk_incidents,
  levels,
  activeDepartment,
  propData,
  is_new,
  getGeneralDashboardDataFN,
  getDepartmentDashboardDataFN,
  getRisks_ReportFN,
  getControls_ReportFN,
  getActions_ReportFN,
  getIncidents_ReportFN,
  getLevelsFN,
  createReportFN,
  saveReportFN,
}) {
  const { i18n } = useTranslation();
  const [loading, setLoading] = useState(true);

  /** Aligns with app i18n (en | tr) for report template / editor chrome */
  const appReportLanguage = useMemo(() => {
    const lng = (i18n.resolvedLanguage || i18n.language || "en").toLowerCase();
    return lng.startsWith("tr") ? "tr" : "en";
  }, [i18n.resolvedLanguage, i18n.language]);

  // Fetch data on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        await Promise.all([
          getGeneralDashboardDataFN(),
          getDepartmentDashboardDataFN(department_id),
          getRisks_ReportFN(department_id),
          getControls_ReportFN(department_id),
          getActions_ReportFN(department_id),
          getIncidents_ReportFN(department_id),
          getLevelsFN(),
        ]);
      } catch (error) {
        console.error('Error fetching report data:', error);
      } finally {
        setTimeout(() => setLoading(false), 800);
      }
    };

    fetchData();
  }, [
    department_id,
    getGeneralDashboardDataFN,
    getDepartmentDashboardDataFN,
    getRisks_ReportFN,
    getControls_ReportFN,
    getActions_ReportFN,
    getIncidents_ReportFN,
    getLevelsFN,
  ]);

  // Prepare chart data for blocks
  const chartData = {
    risks_by_department: general_dashboard_data?.risks_by_department,
    risk_level_by_department: general_dashboard_data?.risk_level_by_department,
    risk_assessment_history: general_dashboard_data?.risk_assessment_history,
    risk_appetite: general_dashboard_data?.risk_appetite,
    risk_summary: department_dashboard_data?.risk_summary,
    summary_for_report: department_dashboard_data?.summary_for_report,
    top_5_risks: department_dashboard_data?.top_5_risks,
    action_status: department_dashboard_data?.action_status,
  };

  // Prepare report data for tables
  const reportData = {
    risks: risks_report,
    controls: risk_controls,
    actions: risk_actions,
    incidents: risk_incidents,
    levels,
  };

  // Handle save
  const handleSave = useCallback(async (blocks, language = 'tr') => {
    const reportJSON = {
      blocks,
      metadata: {
        updatedAt: new Date().toISOString(),
        departmentId: department_id,
        language: language, // Save the language with the report
        version: '2.0',
      },
    };

    try {
      // Check if this is an existing report (has an ID)
      const reportId = propData?.id || propData?._id || propData?.report_id;
      
      if (reportId && !is_new) {
        // Update existing report
        await saveReportFN(reportId, reportJSON);
        //console.log('Report updated successfully');
      } else {
        // Create new report
        const title = blocks?.find(b => b.type === 'hero')?.content?.title || 'New Report';
        await createReportFN(title, reportJSON, 1);
        //console.log('Report created successfully');
      }
    } catch (error) {
      console.error('Error saving report:', error);
      throw error;
    }
  }, [createReportFN, saveReportFN, department_id, propData, is_new]);

  // Handle close - this will be called with blocks data from PlateReportEditor
  const handleClose = useCallback((reportData = null) => {
    if (closeReport) {
      if (reportData && reportData.blocks) {
        // If we have report data, pass it in the expected format
        const reportJSON = {
          blocks: reportData.blocks,
          metadata: {
            ...reportData.metadata,
            updatedAt: new Date().toISOString(),
            departmentId: department_id,
            version: '2.0',
          },
        };
        closeReport(reportJSON);
      } else {
        // Just close without saving
        closeReport(null);
      }
    }
  }, [closeReport, department_id]);

  // Create initial blocks - return saved data for existing reports, empty for new ones
  const getInitialBlocks = useCallback(() => {
    //console.log('getInitialBlocks called with:', { is_new, hasPropData: !!propData });
    
    // For NEW reports, return empty to generate fresh template
    if (is_new || !propData) {
      //console.log('New report or no propData - generating fresh template');
      return [];
    }
    
    // For EXISTING reports, try to load saved data
    try {
      // Check if propData has saved content (backend returns it in 'json' field)
      const savedContent = propData.json || propData.html_content;
      //console.log('Saved content found:', !!savedContent, typeof savedContent);
      
      if (savedContent) {
        // Parse if it's a string
        const parsedContent = typeof savedContent === 'string' 
          ? JSON.parse(savedContent) 
          : savedContent;
        
        //console.log('Parsed content keys:', parsedContent ? Object.keys(parsedContent) : 'null');
        
        // Check for new format (blocks array)
        if (parsedContent?.blocks && Array.isArray(parsedContent.blocks)) {
          //console.log('Loading saved blocks:', parsedContent.blocks.length);
          return parsedContent.blocks;
        }
        
        // Check for old format and convert
        if (parsedContent?.editableName) {
          //console.log('Converting old format to blocks');
          return convertOldFormatToBlocks(parsedContent);
        }
        
        //console.log('Unknown content format, generating fresh template');
      }
    } catch (error) {
      console.error('Error loading saved report data:', error);
    }
    
    // Fallback to empty (will generate fresh template)
    return [];
  }, [is_new, propData]);

  // Convert old report format to new block format
  const convertOldFormatToBlocks = (json) => {
    const e = json.editableName || {};
    const other = json.other || {};
    const blocks = [];
    const ts = Date.now();

    // Default content values (matching ReportOverlay.js defaults)
    const defaults = {
      page2subtitle2: 'Climate-related events remain a primary concern, as extreme weather patterns—including floods, hurricanes, and wildfires—drive higher-than-anticipated claims frequency and severity. Increased climate volatility continues to test the accuracy of catastrophe modeling tools and strains resources for loss recovery. Rising demands for parametric insurance products indicate a shift towards more adaptable insurance solutions, but also introduce challenges in risk quantification and pricing.',
      page2subtitle4: 'Global political tensions and regulatory shifts in key markets, including Europe and North America, have introduced uncertainty for multinational reinsurance programs. Sanctions, trade restrictions, and geopolitical conflicts may impact the availability of reinsurance for certain sectors, such as energy and infrastructure. Emerging markets, where regulatory frameworks are evolving, present both growth opportunities and heightened regulatory risk, as new compliance standards must be met across diverse jurisdictions.',
      page2subtitle6: 'Heightened regulatory scrutiny on data privacy, solvency requirements, and transparency in reinsurance transactions has necessitated increased compliance monitoring and reporting efforts.',
      page2subtitle8: 'Increasing sophistication in cyberattacks poses a substantial risk to operational continuity, client data protection, and financial stability. Cyber risks are not only a concern for internal operations but also for client portfolios, as demand for cyber reinsurance products grows.',
      page3subtitle3: 'Risk appetite refers to the types and amount of risk, on a broad level, that the organization is willing to accept in pursuit of value. Neither appetite nor risk tolerance — the acceptable levels of variation in performance related to business objectives — is typically defined at the risk-specific level.',
      page3subtitle4: `<ul><li class="list-item"><div class="circle averse"></div><strong>Averse: No Risk Appetite</strong> – Prioritizes security and stability, actively avoiding risks and favoring highly controlled environments.</li><li class="list-item"><div class="circle minimalist"></div><strong>Minimalist: Very Low Risk Appetite</strong> – Focuses on essential, low-impact risks, maintaining a lean approach to risk-taking, only as necessary.</li><li class="list-item"><div class="circle cautious"></div><strong>Cautious: Low Risk Appetite</strong> – Takes calculated risks with substantial preventive measures, preferring known outcomes with minimal uncertainty.</li><li class="list-item"><div class="circle open"></div><strong>Open: Moderate Risk Appetite</strong> – Willing to take some risks, considering innovative opportunities while balancing potential gains and losses.</li><li class="list-item"><div class="circle hungry"></div><strong>Hungry: High Risk Appetite</strong> – Actively seeks risk for the potential of high rewards, tolerating significant uncertainty and variability in outcomes.</li></ul>`,
      page3subtitle6: `<ul style="list-style-type: disc; padding-left: 20px; margin: 0;"><li style="margin-bottom: 10px;font-weight: normal;"><strong>Operational Risks:</strong> Potential disruption of business operations from plant shutdowns, suspensions, debarments, and loss of license.</li><li style="margin-bottom: 10px;font-weight: normal;"><strong>Financial Risks:</strong> Internal and external costs associated with investigating and remediation (e.g., legal fees, consultants, investigators).</li><li style="margin-bottom: 10px;font-weight: normal;"><strong>Strategic Risks:</strong> Prohibition to add new customers, loss of license.</li><li style="margin-bottom: 10px;font-weight: normal;"><strong>Compliance Risks:</strong> Report on regulatory compliance risks and any new requirements.</li><li style="margin-bottom: 10px;font-weight: normal;"><strong>Reputational Risks:</strong> Effect of media coverage; damage to organization's image/brand; and subsequent diminished attractiveness to current and potential future employees, business partners, vendors, and customers.</li></ul>`,
      page4subtitle2: `<ul style="list-style-type: disc; padding-left: 20px; margin-bottom: 20px;"><li style="margin-bottom: 10px;font-weight:normal"><strong>Inherent Risk:</strong> The level of risk that exists before any controls or mitigating actions are applied. It represents the raw, unaltered level of exposure to a particular threat or vulnerability.</li><li style="margin-bottom: 10px;font-weight:normal"><strong>Residual Risk:</strong> The level of risk that remains after controls and mitigating actions have been implemented. It reflects the organization's exposure after measures are taken to reduce or manage inherent risks.</li><li style="margin-bottom: 10px;font-weight:normal"><strong>Future Risk:</strong> The Future Risk or Target Risk Level refers to the level of risk that an organization aims to achieve or maintain in the future, considering upcoming changes, emerging trends, or potential unforeseen events.</li></ul><p style="margin-bottom:20px"><strong>Key Distinction</strong></p><ul style="list-style-type: disc; padding-left: 20px;"><li style="margin-bottom: 10px;font-weight:normal"><strong>Inherent Risk:</strong> Initial state of risk, pre-mitigation.</li><li style="margin-bottom: 10px;font-weight:normal"><strong>Residual Risk:</strong> Post-mitigation, the remaining risk.</li><li style="margin-bottom: 10px;font-weight:normal"><strong>Future Risk:</strong> Accepted risk level.</li></ul>`,
      page4subtitle3: `<p style="font-weight:normal"><b>Likelihood of occurrence</b> refers to the probability or chance that a specific risk event will happen.</p>`,
      page5subtitle4: 'The organization\'s risk appetite would also be reflected in setting the values used in the anchor labels. An additional factor that may enhance the evaluation of severity is the localization or regionalization of the assessment. For multilocation and multinational organizations, risk may vary from one location or region to another, based on a wide variety of factors.',
      page5subtitle5: `<ul style="list-style-type: disc; padding-left: 20px; margin-bottom: 20px;font-weight:normal"><li style="margin-bottom: 10px;"><strong>Purpose:</strong> Visualize and categorize risks to prioritize which ones require more attention.</li><li style="margin-bottom: 10px;"><strong>Matrix Design:</strong> A typical matrix includes "Likelihood" on one axis and "Impact" on the other, with the resulting cells indicating a low, medium, or high overall risk.</li><li style="margin-bottom: 10px;"><strong>Prioritization:</strong> Risks in the high-likelihood, high-impact quadrant are top priorities, while low-likelihood, low-impact risks may require minimal action.</li></ul>`,
      page6subtitle2: 'This section presents the company\'s general risk profile, including departmental risk counts, inherent risk levels, and risk appetite. A five-quarter time series shows inherent, residual, and future risk levels, helping track risk trends and assess alignment with the company\'s acceptable risk threshold across departments.',
      page6subtitle3: `<p style="font-weight:bold">Residual risk by departments:</p><p style="font-weight:normal">Residual risk is the amount of risk that remains after all risk controls and mitigation efforts have been implemented. In other words, it's the level of exposure that persists even after preventive, detective, and corrective measures are in place. We use residual risk as a KPI of risk awareness reflects how well employees and the organization recognize and address potential risks, highlighting a proactive approach to risk management.</p><p style="font-weight:normal">The charts indicate that the marketing and finance departments have higher levels of residual risk, highlighting the need for greater focus and improvement in these departments.</p>`,
      page6subtitle4: `<p style="font-weight:bold">General risk level by time:</p><p style="font-weight:normal">The above graph shows the average inherent risks, residual risks and future risks of the company in 5 quarters. During the reporting period, the inherent risks and residual risks of the company have decreased compared to the previous period. Our company has made serious progress in reducing risks.</p>`,
      page6subtitle5: `<p style="font-weight:bold">Risk appetite:</p><p style="font-weight:normal">Our company mostly operates with a minimalist risk appetite, prioritizing only essential and low-impact risks. This lean approach ensures that our risk-taking is deliberate and necessary, aligning with our core objectives and preserving organizational stability.</p>`,
      page7subtitle1: 'An overview of the general risk levels across the department is presented in this section, highlighting key statistics and assessments. A list of strategic risks that may impact long-term goals is also outlined, ensuring preparedness to address these challenges and maintain alignment with the broader business strategy.',
      page7subtitle3: 'This section provides an overview of the inherent, residual, and future risk levels for financial, operational, and reputational risks. The primary key performance indicator (KPI) is the effectiveness of mitigation strategies, evaluated based on the ability to reduce inherent risks to future risk levels.',
      page8subtitle2: 'This section comprises risks that have been mitigated to an acceptable level, based on their inherent, residual, and future risk evaluations. These risks, while previously higher in severity, have been reduced through effective controls and interventions.',
      page8subtitle3: 'Ongoing monitoring ensures that these risks remain within acceptable thresholds, with measures in place to address any potential changes in the risk landscape. The goal is to maintain these risks at a manageable level, ensuring the continued stability and resilience of the organization.',
      page8subtitle5: 'The strategies and measures implemented to address high-risk areas across financial, operational, and reputational risks are outlined. For each identified risk, the effectiveness of the controls in place to reduce their likelihood and impact has been assessed. Findings from the evaluation process are presented, along with recommendations for enhancing control measures to further mitigate high-risk levels.',
      page8subtitle7: 'For risks that have been successfully reduced to acceptable levels, no further action is required at this stage. These risks are considered adequately mitigated, and efforts will focus on ongoing monitoring to ensure they remain within acceptable thresholds.',
      page9subtitle2: 'After implementing control measures, we have identified that some of certain financial, operational, and reputational risks still remain at a high-risk level. These risks are considered critical, requiring further attention and more robust mitigation strategies to prevent them from impacting the department\'s objectives.',
      page9subtitle3: 'Continuous monitoring and a proactive approach are essential to managing and reducing these risks to acceptable levels.',
      page9subtitle5: 'The strategies and measures implemented to address high-risk areas across financial, operational, and reputational risks are outlined. For each identified risk, the effectiveness of the controls in place to reduce their likelihood and impact has been assessed. Findings from the evaluation process are presented, along with recommendations for enhancing control measures to further mitigate high-risk levels.',
      page9subtitle7: 'All actions taken to address identified risks, along with their respective start and due times, are outlined. The progress of each action is tracked, with its status indicated as completed, in progress, or facing challenges.',
      page9subtitle8: 'By regularly updating the status of each action, we can ensure that necessary adjustments are made and maintain transparency in the execution of our risk management plan.',
      page10subtitle1: 'Incidents that occurred during this reporting period are shown in this section.',
      page11subtitle1: `<p class="mb-4">As part of our ongoing risk management efforts, the following provides a summary of the key findings, actions taken, and future steps for addressing financial, operational, and reputational risks.</p><ol class="list-decimal list-inside space-y-3"><li>A thorough assessment of the organization's financial, operational, and reputational risks has been completed, providing a clear view of the current risk landscape.</li><li>The effectiveness of implemented controls has been evaluated, showing significant reductions in risk levels. Several risks have successfully been mitigated from high to moderate or low levels, indicating that control measures have been largely effective.</li><li>Despite the progress, certain risks, particularly in operational and reputational areas, still require ongoing attention. These areas necessitate continuous monitoring and refinement of controls to align with the company's risk tolerance and ensure long-term stability.</li><li>Specific recommendations have been provided to address the remaining high-risk areas. These suggestions focus on enhancing the current controls and implementing new measures where necessary to further reduce the likelihood and impact of these risks.</li><li>An action plan has been put in place, with clearly defined actions and timelines for each risk area. These actions aim to further mitigate the risks and ensure that they remain within acceptable levels.</li><li>Moving forward, the organization will continue to prioritize risk management, focusing on maintaining risks at acceptable levels. Any changes in the risk environment will be promptly addressed to ensure that the company's risk exposure remains within acceptable boundaries.</li></ol>`,
    };

    // ========== PAGE 1 - HERO/COVER ==========
    blocks.push({
      id: `hero-${ts}`,
      type: BLOCK_TYPES.HERO,
      content: {
        title: e.title || 'Q1 - REPORT',
        subtitle: e.subtitle || 'Comprehensive Risk Insights',
        backgroundImage: 'mountain',
        overlayColor: '#bb133e',
        overlayOpacity: 0.75,
      },
    });

    blocks.push({ id: `divider-1-${ts}`, type: BLOCK_TYPES.DIVIDER, settings: { style: 'solid', thickness: 1, color: '#e5e7eb', spacing: 24 } });

    // ========== PAGE 2 - EXECUTIVE SUMMARY ==========
    // Default values for executive summary
    const defaultPage1subtitle2 = 'Identify, Assess, Mitigate, and Monitor – Empowering Strategic Decisions and Ensuring Resilience.';
    const defaultPage1subtitle3 = 'Objectives of an ERM risk report include identifying and categorizing risks, assessing their impact and likelihood, monitoring trends, recommending mitigation strategies, supporting strategic decision-making, and ensuring compliance and accountability.';
    const defaultPage1subtitle5 = 'Key highlights of this report include a comprehensive identification and categorization of current and emerging risks across the organization, with detailed assessments of each risk\'s potential impact and likelihood. We provide actionable mitigation strategies aimed at reducing vulnerability and enhancing resilience. Trends in risk exposure are monitored to keep pace with evolving challenges, and insights are aligned with organizational objectives to support strategic decision-making. Additionally, our approach emphasizes compliance and accountability, ensuring that all risk management activities are transparent, well-documented, and aligned with regulatory standards. This proactive and structured approach positions the organization for sustainable growth and operational security.';

    // Full-width header + objectives at top
    blocks.push({
      id: `text-exec-header-${ts}`,
      type: BLOCK_TYPES.TEXT,
      content: {
        html: `<h2 style="color: #bb133e; font-weight: bold; font-size: 1rem;">${e.page1title || '1-EXECUTIVE SUMMARY'}</h2>
               <p style="color: #bb133e; font-weight: bold;">${e.page1subtitle1 || 'Objectives'}</p>
               <p style="font-weight: bold;">${e.page1subtitle2 || defaultPage1subtitle2}</p>
               <p>${e.page1subtitle3 || defaultPage1subtitle3}</p>`,
      },
    });

    // 2-column layout below: Key Highlights (left) + image (right)
    blocks.push({
      id: `columns-exec-${ts}`,
      type: BLOCK_TYPES.COLUMNS,
      content: {
        preset: '2-equal',
        columns: [
          {
            id: `col-exec-1-${ts}`,
            width: 50,
            blocks: [
              {
                id: `text-exec-highlights-${ts}`,
                type: BLOCK_TYPES.TEXT,
                content: {
                  html: `<p style="color: #bb133e; font-weight: bold;">${e.page1subtitle4 || 'Key Highlights'}</p>
                         <p>${e.page1subtitle5 || defaultPage1subtitle5}</p>`,
                },
              },
            ],
          },
          {
            id: `col-exec-2-${ts}`,
            width: 50,
            blocks: [
              {
                id: `img-page1chart-${ts}`,
                type: BLOCK_TYPES.IMAGE,
                content: { src: 'page1chart', alt: 'Executive Summary Word Cloud' },
              },
            ],
          },
        ],
      },
    });

    blocks.push({ id: `divider-2-${ts}`, type: BLOCK_TYPES.DIVIDER, settings: { style: 'solid', thickness: 1, color: '#e5e7eb', spacing: 24 } });

    // ========== PAGE 3 - RISK ENVIRONMENT OVERVIEW ==========
    blocks.push({
      id: `text-risk-env-header-${ts}`,
      type: BLOCK_TYPES.TEXT,
      content: {
        html: `<h2 style="color: #bb133e; font-weight: bold; font-size: 1rem;">${e.page2title || '2-RISK ENVIRONMENT OVERVIEW'}</h2>
               <p style="color: #bb133e; font-weight: bold;">${e.page2subtitle1 || 'Climate Change and Catastrophe Exposure:'}</p>
               <p>${e.page2subtitle2 || defaults.page2subtitle2}</p>`,
      },
    });

    // 3-column layout matching old editor
    blocks.push({
      id: `columns-risk-env-${ts}`,
      type: BLOCK_TYPES.COLUMNS,
      content: {
        preset: '3-equal',
        columns: [
          {
            id: `col-risk-env-1-${ts}`,
            width: 33,
            blocks: [
              {
                id: `text-risk-env-geo-${ts}`,
                type: BLOCK_TYPES.TEXT,
                content: {
                  html: `<p style="color: #bb133e; font-weight: bold;">${e.page2subtitle3 || 'Geopolitical Uncertainty:'}</p>
                         <p>${e.page2subtitle4 || defaults.page2subtitle4}</p>`,
                },
              },
            ],
          },
          {
            id: `col-risk-env-2-${ts}`,
            width: 34,
            blocks: [
              {
                id: `img-page2chart-${ts}`,
                type: BLOCK_TYPES.IMAGE,
                content: { src: 'page2chart', alt: 'Page 2 Chart' },
              },
              {
                id: `text-risk-env-cyber-${ts}`,
                type: BLOCK_TYPES.TEXT,
                content: {
                  html: `<p style="color: #bb133e; font-weight: bold; margin-top: 16px;">${e.page2subtitle7 || 'Cybersecurity Threats:'}</p>
                         <p>${e.page2subtitle8 || defaults.page2subtitle8}</p>`,
                },
              },
            ],
          },
          {
            id: `col-risk-env-3-${ts}`,
            width: 33,
            blocks: [
              {
                id: `text-risk-env-reg-${ts}`,
                type: BLOCK_TYPES.TEXT,
                content: {
                  html: `<p style="color: #bb133e; font-weight: bold;">${e.page2subtitle5 || 'Regulatory and Compliance:'}</p>
                         <p>${e.page2subtitle6 || defaults.page2subtitle6}</p>`,
                },
              },
            ],
          },
        ],
      },
    });

    blocks.push({ id: `divider-3-${ts}`, type: BLOCK_TYPES.DIVIDER, settings: { style: 'solid', thickness: 1, color: '#e5e7eb', spacing: 24 } });

    // ========== PAGE 4 - STRATEGY AND OBJECTIVE ==========
    blocks.push({
      id: `text-strategy-header-${ts}`,
      type: BLOCK_TYPES.TEXT,
      content: {
        html: `<h2 style="color: #bb133e; font-weight: bold; font-size: 1rem;">${e.page3title || '3-STRATEGY AND OBJECTIVE'}</h2>
               <p style="color: #bb133e; font-weight: bold;">${e.page3subtitle1 || 'Risk Appetite, Risk Category'}</p>
               <p style="color: #bb133e; font-weight: bold; margin-top: 16px;">${e.page3subtitle2 || 'Risk Appetite Definition'}</p>
               <p>${e.page3subtitle3 || defaults.page3subtitle3}</p>`,
      },
    });

    // 2-column layout: page3chart image + Risk appetite list
    blocks.push({
      id: `columns-strategy-${ts}`,
      type: BLOCK_TYPES.COLUMNS,
      content: {
        preset: '2-equal',
        columns: [
          {
            id: `col-strategy-1-${ts}`,
            width: 50,
            blocks: [
              {
                id: `img-page3chart-${ts}`,
                type: BLOCK_TYPES.IMAGE,
                content: { src: 'page3chart', alt: 'Risk Appetite Chart' },
              },
            ],
          },
          {
            id: `col-strategy-2-${ts}`,
            width: 50,
            blocks: [
              {
                id: `text-strategy-appetite-${ts}`,
                type: BLOCK_TYPES.TEXT,
                content: {
                  html: `<div style="margin: 16px 0;">${e.page3subtitle4 || defaults.page3subtitle4}</div>`,
                },
              },
            ],
          },
        ],
      },
    });

    blocks.push({
      id: `text-strategy-category-${ts}`,
      type: BLOCK_TYPES.TEXT,
      content: {
        html: `<p style="color: #bb133e; font-weight: bold; margin-top: 16px;">${e.page3subtitle5 || 'Risk Category Definition'}</p>
               <div>${e.page3subtitle6 || defaults.page3subtitle6}</div>`,
      },
    });

    blocks.push({ id: `divider-4-${ts}`, type: BLOCK_TYPES.DIVIDER, settings: { style: 'solid', thickness: 1, color: '#e5e7eb', spacing: 24 } });

    // ========== PAGE 5 - PERFORMANCE ==========
    blocks.push({
      id: `text-performance-${ts}`,
      type: BLOCK_TYPES.TEXT,
      content: {
        html: `<h2 style="color: #bb133e; font-weight: bold; font-size: 1rem;">${e.page4title || '4-PERFORMANCE'}</h2>
               <p style="color: #bb133e; font-weight: bold;">${e.page4subtitle1 || 'Method and severity of risk'}</p>
               <div style="margin: 16px 0;">${e.page4subtitle2 || defaults.page4subtitle2}</div>
               <div style="margin: 16px 0;">${e.page4subtitle3 || defaults.page4subtitle3}</div>`,
      },
    });

    // 2-column layout: Probability table + page4chart image
    blocks.push({
      id: `columns-performance-${ts}`,
      type: BLOCK_TYPES.COLUMNS,
      content: {
        preset: '2-equal',
        columns: [
          {
            id: `col-perf-1-${ts}`,
            width: 50,
            blocks: [
              {
                id: `text-perf-table-${ts}`,
                type: BLOCK_TYPES.TEXT,
                content: {
                  html: `<div>${other.page4subtitle4 || ''}</div>`,
                },
              },
            ],
          },
          {
            id: `col-perf-2-${ts}`,
            width: 50,
            blocks: [
              {
                id: `img-page4chart-${ts}`,
                type: BLOCK_TYPES.IMAGE,
                content: { src: 'page4chart', alt: 'Risk Matrix Chart' },
              },
            ],
          },
        ],
      },
    });

    blocks.push({ id: `divider-5-${ts}`, type: BLOCK_TYPES.DIVIDER, settings: { style: 'solid', thickness: 1, color: '#e5e7eb', spacing: 24 } });

    // ========== PAGE 6 - KEY RISK CATEGORIES ==========
    blocks.push({
      id: `text-risk-categories-${ts}`,
      type: BLOCK_TYPES.TEXT,
      content: {
        html: `<h2 style="color: #bb133e; font-weight: bold; font-size: 1rem;">${e.page5title || '5-KEY RISK CATEGORIES'}</h2>
               <p style="color: #bb133e; font-weight: bold;">${e.page5subtitle1 || 'Severity of risk'}</p>
               <p style="font-weight: bold;">${e.page5subtitle2 || 'Operational, Financial, Reputational Risks Impact'}</p>
               <div style="margin: 16px 0;">${other.page5subtitle3 || ''}</div>`,
      },
    });

    // 2-column layout: text + page5chart image
    blocks.push({
      id: `columns-risk-cat-${ts}`,
      type: BLOCK_TYPES.COLUMNS,
      content: {
        preset: '2-equal',
        columns: [
          {
            id: `col-risk-cat-1-${ts}`,
            width: 50,
            blocks: [
              {
                id: `text-risk-cat-details-${ts}`,
                type: BLOCK_TYPES.TEXT,
                content: {
                  html: `<p>${e.page5subtitle4 || defaults.page5subtitle4}</p>
                         <div style="margin-top: 16px;">${e.page5subtitle5 || defaults.page5subtitle5}</div>`,
                },
              },
            ],
          },
          {
            id: `col-risk-cat-2-${ts}`,
            width: 50,
            blocks: [
              {
                id: `img-page5chart-${ts}`,
                type: BLOCK_TYPES.IMAGE,
                content: { src: 'page5chart', alt: 'Risk Categories Chart' },
              },
            ],
          },
        ],
      },
    });

    blocks.push({ id: `divider-6-${ts}`, type: BLOCK_TYPES.DIVIDER, settings: { style: 'solid', thickness: 1, color: '#e5e7eb', spacing: 24 } });

    // ========== PAGE 7 - COMPANY RISK LEVEL ==========
    blocks.push({
      id: `text-company-risk-${ts}`,
      type: BLOCK_TYPES.TEXT,
      content: {
        html: `<h2 style="color: #bb133e; font-weight: bold; font-size: 1rem;">${e.page6title || '6-COMPANY RISK LEVEL'}</h2>
               <p style="color: #bb133e; font-weight: bold;">${e.page6subtitle1 || 'Performance'}</p>
               <p>${e.page6subtitle2 || defaults.page6subtitle2}</p>`,
      },
    });

    blocks.push({
      id: `chart-risk-by-dept-${ts}`,
      type: BLOCK_TYPES.CHART,
      content: { chartType: 'RiskByDepartment' },
    });

    blocks.push({
      id: `text-company-risk-2-${ts}`,
      type: BLOCK_TYPES.TEXT,
      content: {
        html: `<div style="margin: 16px 0;">${e.page6subtitle3 || defaults.page6subtitle3}</div>`,
      },
    });

    blocks.push({
      id: `chart-risk-line-${ts}`,
      type: BLOCK_TYPES.CHART,
      content: { chartType: 'RiskLineChart' },
    });

    blocks.push({
      id: `text-company-risk-3-${ts}`,
      type: BLOCK_TYPES.TEXT,
      content: {
        html: `<div style="margin: 16px 0;">${e.page6subtitle4 || defaults.page6subtitle4}</div>`,
      },
    });

    blocks.push({
      id: `chart-risk-appetite-${ts}`,
      type: BLOCK_TYPES.CHART,
      content: { chartType: 'RiskAppetite' },
    });

    blocks.push({
      id: `text-company-risk-4-${ts}`,
      type: BLOCK_TYPES.TEXT,
      content: {
        html: `<div style="margin: 16px 0;">${e.page6subtitle5 || defaults.page6subtitle5}</div>`,
      },
    });

    blocks.push({ id: `divider-7-${ts}`, type: BLOCK_TYPES.DIVIDER, settings: { style: 'solid', thickness: 1, color: '#e5e7eb', spacing: 24 } });

    // ========== PAGE 8 - DEPARTMENTAL DETAIL ==========
    blocks.push({
      id: `text-dept-detail-${ts}`,
      type: BLOCK_TYPES.TEXT,
      content: {
        html: `<h2 style="color: #bb133e; font-weight: bold; font-size: 1rem;">${e.page7title || '7-DEPARTMENTAL DETAIL'}</h2>
               <p>${e.page7subtitle1 || defaults.page7subtitle1}</p>
               <p style="color: #bb133e; font-weight: bold; margin-top: 16px;">${e.page7subtitle2 || 'Risk by department'}</p>
               <p>${e.page7subtitle3 || defaults.page7subtitle3}</p>`,
      },
    });

    blocks.push({
      id: `chart-risk-summary-${ts}`,
      type: BLOCK_TYPES.CHART,
      content: { chartType: 'RiskSummaryReport' },
    });

    blocks.push({
      id: `chart-top-risks-${ts}`,
      type: BLOCK_TYPES.CHART,
      content: { chartType: 'TopRisks' },
    });

    blocks.push({ id: `divider-8-${ts}`, type: BLOCK_TYPES.DIVIDER, settings: { style: 'solid', thickness: 1, color: '#e5e7eb', spacing: 24 } });

    // ========== PAGE 9 - LOW RISK ==========
    blocks.push({
      id: `text-low-risk-${ts}`,
      type: BLOCK_TYPES.TEXT,
      content: {
        html: `<h2 style="color: #bb133e; font-weight: bold; font-size: 1rem;">${e.page8title || '8-LOW RISK'}</h2>
               <p style="color: #bb133e; font-weight: bold;">${e.page8subtitle1 || '8.1.Risk Assessment and Analysis'}</p>
               <p>${e.page8subtitle2 || defaults.page8subtitle2}</p>
               <p style="margin-top: 16px;">${e.page8subtitle3 || defaults.page8subtitle3}</p>`,
      },
    });

    blocks.push({
      id: `table-low-risk-${ts}`,
      type: BLOCK_TYPES.TABLE,
      content: { mode: 'low_risks', title: 'Low Risk Assessment' },
    });

    blocks.push({
      id: `text-low-risk-ctrl-${ts}`,
      type: BLOCK_TYPES.TEXT,
      content: {
        html: `<p style="color: #bb133e; font-weight: bold;">${e.page8subtitle4 || '8.2. Control and Mitigations'}</p>
               <p>${e.page8subtitle5 || defaults.page8subtitle5}</p>`,
      },
    });

    blocks.push({
      id: `table-low-risk-controls-${ts}`,
      type: BLOCK_TYPES.TABLE,
      content: { mode: 'low_risk_controls', title: 'Low Risk Controls' },
    });

    blocks.push({
      id: `text-low-risk-action-${ts}`,
      type: BLOCK_TYPES.TEXT,
      content: {
        html: `<p style="color: #bb133e; font-weight: bold;">${e.page8subtitle6 || '8.3. Action Plan and Timeline'}</p>
               <p>${e.page8subtitle7 || defaults.page8subtitle7}</p>`,
      },
    });

    blocks.push({ id: `divider-9-${ts}`, type: BLOCK_TYPES.DIVIDER, settings: { style: 'solid', thickness: 1, color: '#e5e7eb', spacing: 24 } });

    // ========== PAGE 10 - HIGH RISK ==========
    blocks.push({
      id: `text-high-risk-${ts}`,
      type: BLOCK_TYPES.TEXT,
      content: {
        html: `<h2 style="color: #bb133e; font-weight: bold; font-size: 1rem;">${e.page9title || '9-HIGH RISK'}</h2>
               <p style="color: #bb133e; font-weight: bold;">${e.page9subtitle1 || '9.1.Risk Assessment and Analysis'}</p>
               <p>${e.page9subtitle2 || defaults.page9subtitle2}</p>
               <p style="margin-top: 16px;">${e.page9subtitle3 || defaults.page9subtitle3}</p>`,
      },
    });

    blocks.push({
      id: `table-high-risk-${ts}`,
      type: BLOCK_TYPES.TABLE,
      content: { mode: 'high_risks', title: 'High Risk Assessment' },
    });

    blocks.push({
      id: `text-high-risk-ctrl-${ts}`,
      type: BLOCK_TYPES.TEXT,
      content: {
        html: `<p style="color: #bb133e; font-weight: bold;">${e.page9subtitle4 || '9.2. Control and Mitigations'}</p>
               <p>${e.page9subtitle5 || defaults.page9subtitle5}</p>`,
      },
    });

    blocks.push({
      id: `table-high-risk-controls-${ts}`,
      type: BLOCK_TYPES.TABLE,
      content: { mode: 'high_risk_controls', title: 'High Risk Controls' },
    });

    blocks.push({
      id: `text-high-risk-action-${ts}`,
      type: BLOCK_TYPES.TEXT,
      content: {
        html: `<p style="color: #bb133e; font-weight: bold;">${e.page9subtitle6 || '9.3. Action Plan and Timeline'}</p>
               <p>${e.page9subtitle7 || defaults.page9subtitle7}</p>
               <p style="margin-top: 16px;">${e.page9subtitle8 || defaults.page9subtitle8}</p>`,
      },
    });

    blocks.push({
      id: `table-actions-${ts}`,
      type: BLOCK_TYPES.TABLE,
      content: { mode: 'actions', title: 'Action Plan' },
    });

    blocks.push({
      id: `chart-action-status-${ts}`,
      type: BLOCK_TYPES.CHART,
      content: { chartType: 'ActionStatus' },
    });

    blocks.push({ id: `divider-10-${ts}`, type: BLOCK_TYPES.DIVIDER, settings: { style: 'solid', thickness: 1, color: '#e5e7eb', spacing: 24 } });

    // ========== PAGE 11 - INCIDENTS ==========
    blocks.push({
      id: `text-incidents-${ts}`,
      type: BLOCK_TYPES.TEXT,
      content: {
        html: `<h2 style="color: #bb133e; font-weight: bold; font-size: 1rem;">${e.page10title || '10-INCIDENTS'}</h2>
               <p>${e.page10subtitle1 || defaults.page10subtitle1}</p>`,
      },
    });

    blocks.push({
      id: `table-incidents-${ts}`,
      type: BLOCK_TYPES.TABLE,
      content: { mode: 'incidents', title: 'Incidents' },
    });

    blocks.push({ id: `divider-11-${ts}`, type: BLOCK_TYPES.DIVIDER, settings: { style: 'solid', thickness: 1, color: '#e5e7eb', spacing: 24 } });

    // ========== PAGE 12 - CONCLUSION ==========
    blocks.push({
      id: `text-conclusion-${ts}`,
      type: BLOCK_TYPES.TEXT,
      content: {
        html: `<h2 style="color: #bb133e; font-weight: bold; font-size: 1rem;">${e.page11title || 'CONCLUSION'}</h2>
               <div>${e.page11subtitle1 || defaults.page11subtitle1}</div>`,
      },
    });

    return blocks;
  };

  // Create default blocks for new report using translations
  // Default language is Turkish ('tr') to match old editor behavior
  const createDefaultBlocks = (lang = 'tr') => {
    const t = translations[lang];
    
    // Use the same conversion function with translations
    return convertOldFormatToBlocks({ editableName: t, other: {} });
  };

  // Prefer language stored with the report; otherwise follow global app language (i18n)
  const getInitialLanguage = useCallback(() => {
    try {
      let json = propData?.json;
      if (typeof json === "string") {
        json = JSON.parse(json);
      }
      const lang = json?.metadata?.language;
      if (lang === "tr" || lang === "en") return lang;
    } catch {
      /* ignore */
    }
    if (propData?.metadata?.language === "tr" || propData?.metadata?.language === "en") {
      return propData.metadata.language;
    }
    return appReportLanguage;
  }, [propData, appReportLanguage]);

  if (loading) {
    return (
      <div className="report-editor-loading flex items-center justify-center h-screen bg-white">
        <div className="flex flex-col items-center gap-4">
          <img src={spinner} alt="Loading..." className="w-12 h-12" />
          <p className="text-gray-600">Loading Report Editor...</p>
        </div>
      </div>
    );
  }

  // Use the new Plate-based editor
  return (
    <PlateReportEditor
      initialBlocks={getInitialBlocks()}
      initialLanguage={getInitialLanguage()}
            chartData={chartData}
            reportData={reportData}
      activeDepartment={activeDepartment}
      onSave={handleSave}
            onClose={handleClose}
      isNew={is_new}
          />
  );
}

// Redux Connection
const mapStateToProps = (state) => {
  const allDeps = state.department.departments ?? [];
  const dep = allDeps.find(d => d.department_id === state.department.activeDepartmentId);
  //console.log("Risk Report ", state.risk.risks_report,)
  //console.log("Used ",  state.dashboard.general_dashboard_data,)
  return {
    general_dashboard_data: state.dashboard.general_dashboard_data,
    department_dashboard_data: state.dashboard.department_dashboard_data,
    risks_report: state.risk.risks_report,
    risk_controls: state.risk.risk_controls,
    risk_actions: state.risk.risk_actions,
    risk_incidents: state.risk.risk_incidents,
    levels: state.risk.levels,
    activeDepartment: dep ? dep.name : 'N/A',
  };
};

const mapDispatchToProps = (dispatch) => ({
  getGeneralDashboardDataFN: () => dispatch(getGeneralDashboardData()),
  getDepartmentDashboardDataFN: (id) => dispatch(getDepartmentDashboardData(id)),
  getRisks_ReportFN: (id) => dispatch(getRisks_Report(id)),
  getControls_ReportFN: (id) => dispatch(getControls_Report(id)),
  getActions_ReportFN: (id) => dispatch(getActions_Report(id)),
  getIncidents_ReportFN: (id) => dispatch(getIncidents_Report(id)),
  getLevelsFN: () => dispatch(getLevels()),
  createReportFN: (title, json, type) => dispatch(createReport(title, json, type)),
  saveReportFN: (id, json) => dispatch(saveReport(id, json)),
  getReportFN: (id) => dispatch(getReport(id)),
});

const ReportEditor = compose(
  connect(mapStateToProps, mapDispatchToProps)
)(ReportEditorInner);

export default ReportEditor;

